import React, { useEffect, useState } from "react";
import { plyxApi as api } from "@/lib/plyxApi";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * MiniHourButton — broadcaster-only CTA shown on `PlyxLiveNew.jsx`.
 *
 * One-tap activation of a 5-minute creator-scoped 2× multiplier on
 * tips/gifts to this room. Composes multiplicatively with the weekly
 * Live Hour event, capped at 5× combined.
 *
 * Hides itself once `uses_remaining === 0`. Shows a live countdown
 * while the window is active.
 */
const fmtCountdown = (secs) => {
  if (secs <= 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const MiniHourButton = ({ roomName }) => {
  const [state, setState] = useState(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    api.get(`/plyx/live/${encodeURIComponent(roomName)}/mini-hour`)
      .then((r) => setState(r.data))
      .catch(() => {});

  useEffect(() => {
    if (!roomName) return;
    refresh();
    const poll = setInterval(refresh, 30_000);
    const cd = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(cd); };
  }, [roomName]);

  if (!state) return null;

  const remaining = Math.max(0, (state.seconds_remaining || 0) - tick);
  const canStart = state.uses_remaining > 0 && !state.active;
  const noUses = state.uses_remaining <= 0 && !state.active;

  const handleStart = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/plyx/live/${encodeURIComponent(roomName)}/mini-hour/start`);
      toast.success(`Mini Hour started — ${r.data.multiplier}× for ${Math.round(state.duration_sec / 60)} min`);
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to start Mini Hour";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (state.active) {
    return (
      <div
        data-testid="plyx-mini-hour-active"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FFCC2E] via-[#FF9E2D] to-[#FF3D7F] text-black font-black text-sm shadow-[0_10px_28px_-10px_rgba(255,158,45,0.7)]"
      >
        <Zap className="w-4 h-4 animate-pulse" />
        <span>{state.multiplier}× BOOST</span>
        <span className="px-2 py-0.5 rounded-full bg-black/30 text-[#FFCC2E] tabular-nums text-xs">
          {fmtCountdown(remaining)}
        </span>
      </div>
    );
  }

  if (noUses) {
    return (
      <div
        data-testid="plyx-mini-hour-spent"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] text-white/40 text-xs"
        title="Mini Hour already used on this stream"
      >
        <Zap className="w-3.5 h-3.5" />
        <span>Mini Hour spent</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="plyx-mini-hour-start-btn"
      onClick={handleStart}
      disabled={busy || !canStart}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold text-sm hover:scale-[1.03] active:scale-[0.97] transition-transform disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_22px_-10px_rgba(255,204,46,0.6)]"
      title={`Activate a ${state.multiplier}× boost for ${Math.round(state.duration_sec / 60)} min`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
      <span>Mini Hour · {state.multiplier}×</span>
    </button>
  );
};

export default MiniHourButton;
